import React from 'react'
import {
  ControlledTextInput,
  ControlledTextArea,
  ControlledCheckbox,
} from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { IConfigurableBuilderTest } from 'types/graphqlTypes'
import { useConfigurableBuilderTestOptionsForm } from '../hooks/useConfigurableBuilderTestOptionsForm'

interface IConfigurableBuilderTestOptionsModalProps extends Omit<IModalBaseProps, 'modalType'> {
  options: IConfigurableBuilderTest
  onCancel: () => void
  onSubmit: (options: IConfigurableBuilderTest) => void
}

const useConfigurableBuilderTestModalStyles = makeStyles({
  body: {},
  modal: {},
  header: {},
  title: {},
  root: {},
})

export const ConfigurableBuilderTestOptionsModal = ({
  onCancel,
  onSubmit,
  open = true,
  options,
  ...props
}: IConfigurableBuilderTestOptionsModalProps) => {
  const methods = useConfigurableBuilderTestOptionsForm()
  const { control, errors, handleSubmit } = methods

  const submitHandler = handleSubmit((formParams: IConfigurableBuilderTest) => {
    onSubmit(formParams)
    onCancel()
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const classes = useConfigurableBuilderTestModalStyles()

  return (
    <ModalBase
      dataTest='ConfigurableBuilderTestOptionsModal'
      modalType={MODAL_TYPES.form}
      size={4}
      open={open}
      actions={actions}
      className={classes.root}
      bodyClassName={classes.body}
      titleClass={classes.title}
      modalClassName={classes.modal}
      headerClassName={classes.header}
      title='Builder Test Options'
      {...props}>
      <Box mb={2}>
        <Typography variant='body1'>
          This will save the current input options and JSON output as a builder test. You can re-run
          this test later to be sure that edits to the builder template still produce the same
          output.
        </Typography>
      </Box>
      <Box mb={2}>
        Live: <ControlledCheckbox control={control} name='isLive' defaultValue={options.isLive} />
      </Box>
      <Box mb={2}>
        <ControlledTextInput
          control={control}
          name='name'
          defaultValue={options.name}
          TextInputProps={{
            label: 'Test Name',
            placeholder: 'Test Name',
            required: true,
            large: true,
            validationMessage: errors?.name?.message,
            error: !!errors?.name?.message,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledTextArea
          control={control}
          name='notes'
          defaultValue={options.notes || ''}
          TextAreaProps={{
            label: 'Notes',
            placeholder: 'Describe what scenario this test simulates',
            required: false,
          }}
        />
      </Box>
    </ModalBase>
  )
}
